import { Badge, Box, FlexLayout, H4, Icon } from '@deity/falcon-ui';
import { MappingContext } from 'contexts/MappingContext';
import { useContext, useEffect, useState } from 'react';
import { ErrorNotification } from '../ErrorNotification';
import { FooterItem } from '../FooterItem';
import { countMappedRequiredFields, countMappingErrors, countSchemaFields } from './helpers';

export const MappingSummaryFooter = () => {
  const ctx = useContext(MappingContext);
  const [footerState, setFooterState] = useState<
    | {
        sourceSchemaCount: number;
        targetSchemaCount: number;
        requiredTargetFieldsCount: number;
        mappingCount: number;
        processorCount: number;
        mappedTargetPaths: number;
        errors: { processorError: number; fieldError: number };
        isAllRequiredMapped: boolean;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    if (!ctx) {
      return;
    }
    const targetCount = countSchemaFields(ctx.state.targetSchema);
    const mappingCount = countSchemaFields(ctx.state.mappingSchema);
    const mappedTargetPaths = countMappedRequiredFields(ctx.state.mappingSchema, ctx?.state.targetSchema);
    setFooterState({
      sourceSchemaCount: countSchemaFields(ctx.state.sourceSchema).fields,
      targetSchemaCount: targetCount.fields,
      requiredTargetFieldsCount: targetCount.requiredFields,
      mappingCount: mappingCount.fields,
      processorCount: mappingCount.processors,
      mappedTargetPaths,
      errors: countMappingErrors(ctx.state.structureEditor!, ctx.state.sourceSchema, ctx.state.targetSchema),
      isAllRequiredMapped: mappedTargetPaths === targetCount.requiredFields
    });
  }, [ctx?.state.mappingSchema, ctx?.state.structureHashId]);

  return (
    <FlexLayout justifySelf="center">
      <FooterItem bl="regular" itemTitle="MAPPING & PROCESSORS">
        <FlexLayout alignItems="center" mt="xs">
          <Badge bg="lightGrey" variant="round" color="secondary">
            {footerState?.sourceSchemaCount}
          </Badge>
          <Box as="span" bg="secondary" css={{ height: '2px', width: '16px' }} />
          <Badge bg="white" borderColor="lightGrey" border="regular" variant="round" color="secondary">
            {footerState?.mappingCount}
          </Badge>
          <Icon src="arrowForwardRounded" color="secondary" stroke="transparent" />
          <FlexLayout alignItems="center" border="regular" borderColor="secondary" borderRadius="small" pl="xs" pr="xs">
            <Icon src="shuffle" color="primary" stroke="transparent" mr="xs" />
            <H4 fontSize="md" fontWeight="bold" color="secondary">
              {footerState?.processorCount}
            </H4>
          </FlexLayout>
          <Icon src="arrowForwardRounded" color="secondary" stroke="transparent" />
          <Badge variant="round" bg="lightGrey" color="secondary">
            {footerState?.targetSchemaCount}
          </Badge>
        </FlexLayout>
      </FooterItem>
      <FooterItem bl="regular" itemTitle="REQUIRED TARGET FIELDS">
        <FlexLayout alignItems="center" mt="xs">
          <FlexLayout
            alignItems="center"
            border="regular"
            borderRadius="small"
            pl="xs"
            pr="xs"
            borderColor={footerState?.isAllRequiredMapped ? 'successDark' : 'warningDark'}
            bg={footerState?.isAllRequiredMapped ? 'successLight' : 'warningLight'}
          >
            <Badge
              variant="round"
              bg="white"
              ml="xs"
              color={footerState?.isAllRequiredMapped ? 'successDark' : 'warningTextDark'}
            >
              {footerState?.mappedTargetPaths}
            </Badge>
            <H4 color={footerState?.isAllRequiredMapped ? 'successDark' : 'warningTextDark'}>/</H4>
            <Badge
              variant="round"
              bg="white"
              mr="xs"
              color={footerState?.isAllRequiredMapped ? 'successDark' : 'warningTextDark'}
            >
              {footerState?.requiredTargetFieldsCount}
            </Badge>
            <H4
              fontWeight="semiBold"
              fontSize="xs"
              color={footerState?.isAllRequiredMapped ? 'successDark' : 'warningTextDark'}
            >
              MAPPED
            </H4>
          </FlexLayout>
        </FlexLayout>
      </FooterItem>
      <FooterItem bl="regular" itemTitle="PROCESSOR ERRORS">
        <ErrorNotification errorCount={footerState?.errors?.processorError || 0} />
      </FooterItem>
      <FooterItem bl="regular" br="regular" itemTitle="FIELD ERRORS">
        <ErrorNotification errorCount={footerState?.errors?.fieldError || 0} />
      </FooterItem>
    </FlexLayout>
  );
};

export default MappingSummaryFooter;
